import React, { useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useDebouncedCallback } from 'use-debounce'
import { LEXICAL_NODES } from '../MeetingResults/Lexical/nodes'
import { RichTextToolbarPlugin } from '../MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { EditorState, LexicalEditor } from 'lexical'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
  selectMeetingNote,
  selectMeetingNoteChangedExternally,
  selectMeetingNoteIsLoading,
  setMeetingNoteChangedExternally,
} from '../../../../../store/meetingSummary/meetingSupport'
import { useUpdateMeetingNote } from '../hooks/api'
import './Notepad.scss'

export const Notepad: React.FC = () => {
  const dispatch = useAppDispatch()

  const meetingNote = useAppSelector(selectMeetingNote)
  const isLoading = useAppSelector(selectMeetingNoteIsLoading)
  const notepadChangedExternally = useAppSelector(selectMeetingNoteChangedExternally)

  const updateMeetingNote = useUpdateMeetingNote()

  const editorRef = useRef<LexicalEditor>(null)

  const [changed, setChanged] = useState<boolean>(false)
  const [notepadValue, setNotepadValue] = useState<string>(meetingNote)

  const debounced = useDebouncedCallback(async (notepadValue) => {
    await updateMeetingNote(notepadValue)
  }, 500)

  useEffect(() => {
    if (changed) {
      debounced(notepadValue)
    }
  }, [changed, notepadValue, debounced])

  useEffect(() => {
    setNotepadValue(meetingNote)
  }, [meetingNote])

  useEffect(() => {
    if (notepadChangedExternally && editorRef.current) {
      try {
        const editor = editorRef.current
        const editorState = editor.parseEditorState(meetingNote)
        editor.setEditorState(editorState)
        dispatch(setMeetingNoteChangedExternally(false))
      } catch (e) {
        console.error(e)
      }
    }
  }, [dispatch, editorRef, meetingNote, notepadChangedExternally])

  const handleChange = (editorState: EditorState) => {
    setChanged(true)
    setNotepadValue(JSON.stringify(editorState.toJSON()))
  }

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner size='sm' />
      </div>
    )
  }

  return (
    <WritableLexicalEditor
      ref={editorRef}
      className='p-0 flex-grow-1 align-items-start border-0 h-100 notepad-editor'
      containerClassName='h-100'
      editorContainerClassName='zocks-py-1.25 zocks-px-1.25 mb-4 d-flex flex-grow-1 overflow-y-auto notepad-rich-text-editor-container'
      onChange={handleChange}
      initialEditorState={meetingNote || null}
      lexicalNodes={LEXICAL_NODES}
      header={<RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2 notepad-rich-text-toolbar' />}
    />
  )
}
